/**
 * Master switch for the 50% annual-plan campaign (bar, popup, Subscriptions extra).
 * Set false to hide the campaign without removing the UI code.
 */
export const ANNUAL_DISCOUNT_PROMO_ENABLED = true;

/** Marketing percent shown on campaign surfaces (plan cards still use computed %). */
export const ANNUAL_DISCOUNT_PROMO_PERCENT = 25;

/** Bump if you need a new storage namespace for this promo. */
export const ANNUAL_DISCOUNT_PROMO_CAMPAIGN = "v1";

/** The popup waits this long after the visitor entered the site (per browser tab session). */
export const ANNUAL_DISCOUNT_MODAL_DELAY_MS = 30_000;

const STORAGE_PREFIX = `akinda:annual-promo-${ANNUAL_DISCOUNT_PROMO_CAMPAIGN}`;
const LOGIN_ID_KEY = `${STORAGE_PREFIX}:login-id`;
const MODAL_DISMISSED_FOR_KEY = `${STORAGE_PREFIX}:modal-dismissed-for`;
const SITE_ENTERED_AT_KEY = `${STORAGE_PREFIX}:site-entered-at`;

// Fallback when sessionStorage is unavailable (private mode): counts from page load.
let siteEnteredAtInMemory: number | null = null;

function readStorage(key: string): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // private mode / quota
  }
}

/** Call after a successful login / signup so the popup can show again. */
export function markAnnualDiscountPromoLogin(): void {
  writeStorage(
    LOGIN_ID_KEY,
    `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  );
}

export function getAnnualDiscountPromoLoginId(): string {
  return readStorage(LOGIN_ID_KEY) || "guest";
}

export function wasAnnualDiscountModalDismissedForCurrentLogin(): boolean {
  return readStorage(MODAL_DISMISSED_FOR_KEY) === getAnnualDiscountPromoLoginId();
}

export function dismissAnnualDiscountModalForCurrentLogin(): void {
  writeStorage(MODAL_DISMISSED_FOR_KEY, getAnnualDiscountPromoLoginId());
}

/**
 * When the visitor entered the site in this tab, recorded on first call. Kept in
 * sessionStorage so reloads and switching between the site and app layouts
 * do not restart the popup delay; a new tab or visit starts a new count.
 */
export function getSiteEnteredAt(): number {
  try {
    const stored = Number(window.sessionStorage.getItem(SITE_ENTERED_AT_KEY));
    if (stored > 0) return stored;
    const now = Date.now();
    window.sessionStorage.setItem(SITE_ENTERED_AT_KEY, String(now));
    return now;
  } catch {
    siteEnteredAtInMemory ??= Date.now();
    return siteEnteredAtInMemory;
  }
}

/** Milliseconds left before the popup may open (0 once the delay has passed). */
export function getAnnualDiscountModalRemainingDelayMs(): number {
  const elapsed = Date.now() - getSiteEnteredAt();
  return Math.max(0, ANNUAL_DISCOUNT_MODAL_DELAY_MS - elapsed);
}
